import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../config/roleConfig';
import { Logo } from '../common';
import {
  getRequestByEmail,
  submitRequest,
} from '../../services/demoAccessService';

const DEMO_ACCOUNTS = [
  { role: 'Bank Admin', email: 'admin@equityafrica.com', pass: 'demo123' },
  { role: 'Compliance Maker', email: 'compliance@equityafrica.com', pass: 'demo123' },
  { role: 'Compliance Checker', email: 'checker@equityafrica.com', pass: 'demo123' },
  { role: 'Treasury Dealer', email: 'fx@equityafrica.com', pass: 'demo123' },
  { role: 'Forwards Dealer', email: 'forwards@equityafrica.com', pass: 'demo123' },
  { role: 'Family Office', email: 'admin@horizonimpact.com', pass: 'demo123' },
  { role: 'Individual Investor', email: 'analyst@horizonimpact.com', pass: 'demo123' },
];

const DemoAccessPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Email lookup state
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // null | request object
  const [hasChecked, setHasChecked] = useState(false);

  // Request form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', role: '', reason: '' });
  const [submitted, setSubmitted] = useState(false);

  // Demo login state
  const [loggingIn, setLoggingIn] = useState(null);

  const handleLookup = (e) => {
    e.preventDefault();
    if (!lookupEmail.trim()) return;
    const req = getRequestByEmail(lookupEmail.trim());
    setLookupResult(req);
    setHasChecked(true);
    if (!req) {
      setShowForm(true);
      setFormData(prev => ({ ...prev, email: lookupEmail.trim() }));
    } else {
      setShowForm(false);
    }
  };

  const handleSubmitRequest = (e) => {
    e.preventDefault();
    submitRequest(formData);
    setSubmitted(true);
    setShowForm(false);
  };

  const handleDemoLogin = async (accEmail, accPass) => {
    setLoggingIn(accEmail);
    const result = await login(accEmail, accPass);
    if (result.success) {
      const dashboardPath = getDashboardPath(result.user.role);
      navigate(dashboardPath, { replace: true });
    }
    setLoggingIn(null);
  };

  return (
    <div className="min-h-screen flex font-sans text-black antialiased bg-white">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4D4D4D] relative overflow-hidden border-r border-[#4D4D4D]">
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <Logo variant="white" />
          </div>
          <div className="space-y-6 my-auto">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">Platform demo access portal.</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-sans text-white leading-tight">
              Explore the Forwardsflow platform with guided demo accounts across all roles.
            </h1>
          </div>
          <div className="pt-6 border-t border-white/20 flex items-center justify-between text-xs font-mono text-zinc-300">
            <span>&copy; 2026 Forwards Flow Inc.</span>
            <span>Demo access is subject to approval.</span>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>

          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Demo Access</span>
            <h2 className="text-2xl font-bold font-sans text-black">Request demo access</h2>
            <p className="text-xs font-mono text-zinc-500 mt-1">Enter your email to check access status or request a demo</p>
          </div>

          <div className="border border-zinc-300 p-8 rounded-none bg-white">
            {/* Approved State — Show demo account buttons */}
            {lookupResult?.status === 'approved' && (
              <div>
                <div className="mb-6 p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                  Access approved for <strong>{lookupResult.email}</strong>. Select a demo account below.
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-3">Demo Accounts</span>
                <div className="flex flex-wrap gap-1.5">
                  {DEMO_ACCOUNTS.map((acc) => (
                    <button
                      key={acc.email}
                      type="button"
                      onClick={() => handleDemoLogin(acc.email, acc.pass)}
                      disabled={loggingIn !== null}
                      className="px-2.5 py-1 bg-zinc-50 border border-zinc-300 hover:border-[#4D4D4D] font-mono text-[10px] uppercase tracking-wider text-black rounded-none transition-colors disabled:opacity-50"
                    >
                      {loggingIn === acc.email ? 'Logging in...' : acc.role}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => { setHasChecked(false); setLookupResult(null); setLookupEmail(''); }}
                  className="mt-6 font-mono text-xs text-zinc-500 hover:text-black underline"
                >
                  Use a different email
                </button>
              </div>
            )}

            {/* Pending State */}
            {lookupResult?.status === 'pending' && (
              <div>
                <div className="mb-4 p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                  Your request is pending review. You will be notified when approved.
                </div>
                <div className="font-mono text-xs text-zinc-500 space-y-1">
                  <p><span className="text-zinc-400 uppercase tracking-wider">Email:</span> {lookupResult.email}</p>
                  <p><span className="text-zinc-400 uppercase tracking-wider">Submitted:</span> {new Date(lookupResult.requestedAt).toLocaleDateString()}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setHasChecked(false); setLookupResult(null); setLookupEmail(''); }}
                  className="mt-6 font-mono text-xs text-zinc-500 hover:text-black underline"
                >
                  Use a different email
                </button>
              </div>
            )}

            {/* Denied State */}
            {lookupResult?.status === 'denied' && (
              <div>
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-none font-mono text-xs text-red-800">
                  Your demo access request has been denied.
                  {lookupResult.notes && <span className="block mt-1">Reason: {lookupResult.notes}</span>}
                </div>
                <div className="font-mono text-xs text-zinc-500 space-y-1">
                  <p><span className="text-zinc-400 uppercase tracking-wider">Email:</span> {lookupResult.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setHasChecked(false); setLookupResult(null); setLookupEmail(''); }}
                  className="mt-6 font-mono text-xs text-zinc-500 hover:text-black underline"
                >
                  Use a different email
                </button>
              </div>
            )}

            {/* Submitted confirmation */}
            {submitted && !hasChecked && (
              <div className="p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                Your request has been submitted. You will be notified when approved.
              </div>
            )}
            {submitted && hasChecked && !lookupResult && (
              <div className="p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                Your request has been submitted. You will be notified when approved.
              </div>
            )}

            {/* Email Lookup Form — shown when no result yet */}
            {!lookupResult && !submitted && (
              <>
                <form onSubmit={handleLookup} className="space-y-6">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                      Your Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={lookupEmail}
                      onChange={(e) => setLookupEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                  >
                    Check Access
                  </button>
                </form>

                {/* Request form — shown after lookup finds no existing request */}
                {showForm && hasChecked && (
                  <div className="mt-8 pt-6 border-t border-zinc-200">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-4">No request found. Submit a new request:</span>
                    <form onSubmit={handleSubmitRequest} className="space-y-4">
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Full Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          readOnly
                          className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black bg-zinc-50 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Company</label>
                        <input
                          type="text"
                          required
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors"
                          placeholder="Acme Corp"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Role / Title</label>
                        <input
                          type="text"
                          required
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors"
                          placeholder="Portfolio Manager"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Reason for Access</label>
                        <textarea
                          required
                          value={formData.reason}
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors resize-none"
                          placeholder="I'd like to evaluate Forwardsflow for our firm..."
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                      >
                        Submit Request
                      </button>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>

          <p className="mt-6 text-center text-xs font-mono text-zinc-400">
            Already have credentials?{' '}
            <a href="/login" className="text-zinc-700 hover:text-black underline">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoAccessPage;
